import { runInAction } from "mobx";
import { ReplayHeldError, type ReplayVerdict } from "./sessionGuard";

/**
 * The offline queue's replay, made safe against a change of person.
 *
 * `@intrasync/stores` 31.0.2's OfflineQueueManager replays queued writes through
 * the service the store wraps (`OfflineServiceStore.syncWithServer`). Three gaps
 * remain in it that HQ closes from this side (and asks the library to close at
 * the source — plan/2026-10-21_FROM-HQ_session-safety-asks-for-stores.md):
 *
 *  1. ACTIVE-OP IDENTITY. The executor receives the queued op, but the service
 *     call that carries it does not: a `remove` carries only an id, so the
 *     session guard used to exempt every `remove` on a store while ANY queued
 *     remove was replaying. {@link OfflineReplayTracker.attach} wraps
 *     `processQueue` so the op being executed is known exactly; a call is a
 *     replay only if it IS that op's call (same `data` object for create/patch,
 *     the op's own id — or, for a temp id the pass remapped, the one remove the
 *     op is making — for remove), and only once.
 *  2. OWNERSHIP AT CALL TIME. The manager decides which ops belong to the
 *     signed-in user ONCE, before its loop. A replay that outlives the logout
 *     flush could execute later ops after the principal changed. The tracker
 *     re-checks the op's owner against the person signed in NOW — before the
 *     library's executor runs (needs nothing but the op), and again when the
 *     op's service call is made — and refuses (with an error the manager reads
 *     as "no server decision": the op stays queued, nothing is charged, the
 *     pass pauses) when they differ.
 *  3. UNOWNED OPS. Ops queued before owner stamping existed, or while signed
 *     out, carry no principal and the manager replays them under whoever signs
 *     in. They are stamped with {@link QUARANTINE_PRINCIPAL}, which matches
 *     nobody, so the manager itself holds them; a person releases them with
 *     {@link OfflineReplayTracker.adoptQuarantined} (RootStore
 *     `adoptQuarantinedOfflineWrites`).
 *
 * FAIL CLOSED. All of this rests on wrapping two methods of the library's
 * manager. If a library version stops that from working (the methods cannot
 * be replaced, or the manager no longer has the parts this reads), replay is
 * switched OFF for the session instead of running unguarded: the host exposes
 * the facade {@link OfflineReplayTracker.attach} returns, whose `processQueue`
 * replays nothing and REJECTS with {@link ReplayDisabledError} (a store reads a
 * resolved pass as "synced", and then clears its pending marks, refetches over
 * its optimistic rows and records a sync time), the queue is left exactly as
 * it is (writes stay queued for a build that can replay them safely), one
 * error is logged, {@link OfflineReplayTracker.replayDisabled} says why, and
 * the host tells the person once (RootStore `warnIfOfflineReplayDisabled`).
 * `offlineReplay.test.ts` drives the pinned library's real manager and store
 * so an upgrade that breaks the wrapping fails a test before it ships.
 */

/** The shape of a queued operation this reads (OfflineQueueManager's). */
export type QueuedOperation = {
  id: string;
  storeName: string;
  method: string;
  entityId?: string;
  clientId?: string;
  data?: unknown;
  principal?: string;
};

type Executor = (op: QueuedOperation) => Promise<boolean>;

/** The parts of OfflineQueueManager this wraps. */
export type ReplayableQueue = {
  processQueue: (
    executor: Executor,
    onConflict?: unknown,
    storeName?: string,
  ) => Promise<void>;
  enqueue: (operation: Record<string, unknown>) => Promise<string>;
  getQueuedOperations: (storeName?: string) => QueuedOperation[];
  ready?: Promise<boolean>;
};

/**
 * A principal no signed-in user can have. The manager holds an op stamped with
 * it (it differs from every real id and from "nobody") instead of replaying it
 * under whoever happens to be signed in.
 */
export const QUARANTINE_PRINCIPAL = "quarantined:no-recorded-owner";

type ActiveReplay = { op: QueuedOperation; used: boolean };

/**
 * A replay pass refused because replay is switched off on this page (see the
 * file comment). `503`, the offline library's transient class, like
 * {@link ReplayHeldError}: nothing was sent, nothing is charged, every op stays
 * queued — and the caller's sync stops at the rejection instead of carrying on
 * as if it had synced.
 */
export class ReplayDisabledError extends Error {
  readonly name = "ReplayDisabled";
  readonly code = 503;
  constructor(reason: string) {
    super(`Offline replay is switched off on this page (${reason}); queued writes are held, not sent.`);
  }
}

/** Library-local ids (`temp_<ms>_<n>`) an offline create is stored under. */
const isLocalId = (id: unknown) => typeof id === "string" && id.startsWith("temp_");

/** What a manager must have for the wrapping to mean anything. */
const REQUIRED_METHODS = ["processQueue", "enqueue", "getQueuedOperations", "persistOp"] as const;

export class OfflineReplayTracker {
  private readonly active = new Set<ActiveReplay>();
  private queue: ReplayableQueue | null = null;
  private disabledFacade: { manager: ReplayableQueue; facade: ReplayableQueue } | null = null;
  /**
   * The quarantine's and the adoption's storage writes, one after another: run
   * side by side, a quarantine stamp still in flight could land after an
   * adoption and put the op back under {@link QUARANTINE_PRINCIPAL}.
   */
  private stampChain: Promise<unknown> = Promise.resolve();

  /**
   * Why offline replay is switched off on this page (fail closed — see the
   * file comment), or null while it runs normally.
   */
  replayDisabled: string | null = null;

  /**
   * @param currentPrincipal who is signed in NOW (RootStore.offlinePrincipal).
   * @param report logs why replay was switched off (once per manager).
   */
  constructor(
    private readonly currentPrincipal: () => string | undefined,
    private readonly report: (message: string, error?: unknown) => void = () => undefined,
  ) {}

  /**
   * Wrap the manager's `processQueue` (to know the op being executed, and
   * re-check its owner) and `enqueue` (to quarantine a write queued while
   * nobody is signed in), and, once its persisted queue has loaded, quarantine
   * the ops that carry no owner. Returns the queue the host must expose as
   * `rootStore.offlineQueueManager`: the manager itself, or — when it cannot be
   * wrapped — a facade over it that never replays (fail closed; nothing
   * downstream would otherwise stop a replay running unguarded, because a call
   * the tracker does not recognise is an ordinary call to the session guard,
   * and an ordinary call made in the current session goes through). Idempotent
   * per manager.
   */
  attach<Q extends ReplayableQueue>(manager: Q | null | undefined): Q | null | undefined {
    if (!manager) return manager;
    if (this.queue === manager) return manager;
    if (this.disabledFacade?.manager === manager) return this.disabledFacade.facade as Q;

    const missing: string[] = REQUIRED_METHODS.filter(
      (name) => typeof (manager as Record<string, unknown>)[name] !== "function",
    );
    if (typeof manager.ready?.then !== "function") missing.push("ready");
    if (missing.length) {
      return this.disable(manager, `the offline queue manager no longer has ${missing.join(", ")}`);
    }

    const processQueue = manager.processQueue.bind(manager);
    const enqueue = manager.enqueue.bind(manager);
    const wrappedProcessQueue = (executor: Executor, onConflict?: unknown, storeName?: string) =>
      processQueue(async (op) => {
        // Needs nothing but the op: holds even if the call matching below
        // stops recognising this op's service call.
        if (!this.ownedByCurrent(op)) throw new ReplayHeldError(op.storeName);
        const entry: ActiveReplay = { op, used: false };
        this.active.add(entry);
        try {
          return await executor(op);
        } finally {
          this.active.delete(entry);
        }
      }, onConflict, storeName);
    const wrappedEnqueue = (operation: Record<string, unknown>) =>
      enqueue(
        operation.principal === undefined && this.currentPrincipal() === undefined
          ? { ...operation, principal: QUARANTINE_PRINCIPAL }
          : operation,
      );
    try {
      for (const [name, value] of [
        ["processQueue", wrappedProcessQueue],
        ["enqueue", wrappedEnqueue],
      ] as const) {
        Object.defineProperty(manager, name, { configurable: true, writable: true, value });
      }
    } catch (error) {
      return this.disable(manager, "the offline queue manager's methods cannot be wrapped", error);
    }
    if (manager.processQueue !== wrappedProcessQueue || manager.enqueue !== wrappedEnqueue) {
      return this.disable(manager, "the offline queue manager ignored the wrapping of its methods");
    }
    this.queue = manager;
    void manager.ready?.then((ok) => {
      if (ok) void this.quarantineUnowned();
    });
    return manager;
  }

  /** Switch replay off for `manager` (see {@link attach}); log it once. */
  private disable<Q extends ReplayableQueue>(manager: Q, reason: string, error?: unknown): Q {
    this.replayDisabled = reason;
    this.report(
      `Offline replay is switched off on this page: ${reason}. Queued offline writes stay queued and are not sent; @intrasync/stores changed in a way HQ's replay safety does not support (offlineReplay.ts).`,
      error,
    );
    const facade = new Proxy({} as Q, {
      // An empty target, so no invariant of the manager's own (a
      // non-configurable method) can stop the facade answering differently.
      get: (_target, key) => {
        if (key === "processQueue") {
          return async () => {
            throw new ReplayDisabledError(reason);
          };
        }
        const value = Reflect.get(manager, key, manager);
        return typeof value === "function" ? value.bind(manager) : value;
      },
      set: (_target, key, value) => Reflect.set(manager, key, value, manager),
      has: (_target, key) => key in manager,
    });
    this.disabledFacade = { manager, facade };
    return facade;
  }

  /** Does this op belong to the person signed in NOW? Unowned ops belong to nobody. */
  private ownedByCurrent(op: QueuedOperation): boolean {
    const owner = op.principal;
    const now = this.currentPrincipal();
    return owner !== undefined && owner !== QUARANTINE_PRINCIPAL && now !== undefined && owner === now;
  }

  /**
   * Is this service call the queue replaying a stored write?
   *  - `false`: no — the session guard treats it as an ordinary call;
   *  - `true`: yes, and it belongs to the person signed in — let it through;
   *  - `"refuse"`: yes, but its owner is not the person signed in (or it has no
   *    owner) — it must not be sent.
   */
  classify(call: { path: string; method: string; id?: unknown; data?: unknown }): ReplayVerdict {
    for (const entry of this.active) {
      if (entry.used || !this.isCallOf(entry.op, call)) continue;
      entry.used = true;
      return this.ownedByCurrent(entry.op) ? true : "refuse";
    }
    return false;
  }

  private isCallOf(
    op: QueuedOperation,
    call: { path: string; method: string; id?: unknown; data?: unknown },
  ): boolean {
    if (op.storeName !== call.path || op.method !== call.method) return false;
    if (call.method === "remove") {
      if (call.id == null || op.entityId == null) return false;
      // The id the op was queued under, or — when the pass remapped a
      // library-local id to the server's — the one remove this op is making.
      return String(op.entityId) === String(call.id) || isLocalId(op.entityId);
    }
    return op.data !== undefined && op.data === call.data;
  }

  /**
   * Queued ops held for want of an owner: stamped with
   * {@link QUARANTINE_PRINCIPAL}, or still carrying none (not yet stamped, or
   * their stamp could not be stored).
   */
  quarantined(): QueuedOperation[] {
    return (this.queue?.getQueuedOperations() ?? []).filter(
      (op) => op.principal === QUARANTINE_PRINCIPAL || op.principal === undefined,
    );
  }

  /**
   * Stamp every op that has no recorded owner with the quarantine marker, so
   * the manager holds them, and persist the stamp. Returns how many.
   *
   * The stamp goes on the in-memory ops FIRST, synchronously, for all of them:
   * this runs from `ready`, and anything else waiting on `ready` (a store's
   * reconnect sync, the logout flush) can start a pass while the stamps below
   * are still being written. An op whose stamp cannot be STORED stays stamped
   * in memory (and is stamped again from storage next load).
   */
  async quarantineUnowned(): Promise<number> {
    const queue = this.queue as (ReplayableQueue & PersistingQueue) | null;
    if (!queue || typeof queue.persistOp !== "function") return 0;
    const unowned = queue.getQueuedOperations().filter((op) => op.principal === undefined);
    if (!unowned.length) return 0;
    // In memory now, not when the chain reaches this call.
    stamp(unowned, QUARANTINE_PRINCIPAL);
    return this.serially(async () => {
      let count = 0;
      for (const op of unowned) {
        // Persist only what is still quarantined: adoptQuarantined() may have
        // handed it to its owner since (it stamps memory at once).
        const current = findOp(queue, op.id);
        if (!current) continue;
        if (current.principal !== QUARANTINE_PRINCIPAL && current.principal !== undefined) continue;
        try {
          await queue.persistOp({ ...current, principal: QUARANTINE_PRINCIPAL });
          count += 1;
        } catch {
          // Stays quarantined in memory; stamped again on the next load.
        }
      }
      return count;
    });
  }

  /**
   * Give every quarantined op to the person signed in now, so it replays under
   * their identity. The deliberate recovery for writes whose owner was never
   * recorded: call it only when you know they are the signed-in person's.
   * Returns how many were released; 0 when nobody is signed in.
   *
   * The ops are theirs in memory at once (so a quarantine still writing its
   * stamps skips them, and a pass started now replays them); the stored copies
   * follow, after any quarantine write already in flight, so none can land
   * last and take an op back. An op whose new owner cannot be STORED (storage
   * full) is still released for this page; it loads quarantined again next
   * time and needs adopting again.
   */
  async adoptQuarantined(): Promise<number> {
    const principal = this.currentPrincipal();
    const queue = this.queue as (ReplayableQueue & PersistingQueue) | null;
    if (principal === undefined || !queue || typeof queue.persistOp !== "function") return 0;
    const adopted = this.quarantined();
    if (!adopted.length) return 0;
    stamp(adopted, principal);
    return this.serially(async () => {
      for (const op of adopted) {
        const current = findOp(queue, op.id);
        if (!current) continue; // replayed (or removed) meanwhile
        // A quarantine write that finished meanwhile replaced the in-memory op
        // with its own copy: hand that one over too.
        if (current.principal !== principal) stamp([current], principal);
        try {
          await queue.persistOp({ ...current, principal });
        } catch {
          // Released in memory for this page; see above.
        }
      }
      return adopted.length;
    });
  }

  /**
   * Resolves once every quarantine/adoption write started so far has settled.
   * The host waits on it with the queue's own restore (RootStore
   * `awaitOfflineQueueRestore`), so nothing acts on a queue whose stamps are
   * still being written.
   */
  stampsSettled(): Promise<void> {
    return this.stampChain.then(() => undefined);
  }

  /** Run `task` after every stamp write queued before it. Never rejects the chain. */
  private serially<T>(task: () => Promise<T>): Promise<T> {
    const run = this.stampChain.then(task, task);
    this.stampChain = run.catch(() => undefined);
    return run;
  }
}

/** Set the owner on in-memory ops; a queue whose ops cannot be written keeps them as they were. */
function stamp(ops: QueuedOperation[], principal: string): void {
  try {
    runInAction(() => {
      for (const op of ops) op.principal = principal;
    });
  } catch {
    // Each stays as it was in memory, and the owner checks in the wrapped
    // processQueue and classify() still refuse an unowned or quarantined op.
  }
}

/**
 * The queue's current object for an op id.
 * shortcut: O(n) per lookup (O(n²) over a loop); fine for an offline queue of
 * tens of writes, index by id if queues reach thousands.
 */
function findOp(queue: ReplayableQueue, id: string): QueuedOperation | undefined {
  return queue.getQueuedOperations().find((candidate) => candidate.id === id);
}

/** OfflineQueueManager.persistOp is `private` in its typings but public at runtime. */
type PersistingQueue = { persistOp?: (op: QueuedOperation) => Promise<void> };

/** The parts of an OfflineServiceStore the mirror pruning touches. */
type PrunableStore = {
  name?: string;
  idField?: string;
  items?: Array<Record<string, unknown>>;
  offlineStorage?: { removeItem: (key: string) => Promise<unknown> } | null;
  pagination?: { enabled?: boolean };
  clearPageData?: () => void;
  bumpPurgeGeneration?: () => void;
  lastFetchAt?: number;
};

/**
 * After a sign-out that had to KEEP a store (it holds unsynced writes), drop
 * from it every cached row the queued writes do not need, in memory and in the
 * mirror — so the next person does not see the rest of the previous person's
 * cached list. Kept: rows a queued op targets (replay reconciles onto them),
 * local-only rows, and tombstones.
 *
 * LIMIT (library ask): the kept rows themselves are still the previous
 * person's. Hiding those needs the store to partition its mirror by owner.
 *
 * Returns the number of rows dropped.
 */
export function pruneRetainedMirror(
  store: PrunableStore | null | undefined,
  queued: QueuedOperation[],
): number {
  if (!store || !Array.isArray(store.items)) return 0;
  const idField = store.idField ?? "id";
  const needed = new Set<string>();
  for (const op of queued) {
    if (op.storeName !== store.name) continue;
    if (op.entityId != null) needed.add(String(op.entityId));
    if (op.clientId != null) needed.add(String(op.clientId));
  }
  const keep = (row: Record<string, unknown>) =>
    needed.has(String(row[idField])) ||
    (row._clientId != null && needed.has(String(row._clientId))) ||
    row._isTemp === true ||
    row._queued === true ||
    row._deleted === true;
  const dropped = store.items.filter((row) => !keep(row));
  if (!dropped.length && !store.pagination?.enabled) return 0;
  store.bumpPurgeGeneration?.();
  runInAction(() => {
    store.items = store.items!.filter(keep);
    if (store.pagination?.enabled) store.clearPageData?.();
    store.lastFetchAt = 0;
  });
  for (const row of dropped) {
    void store.offlineStorage?.removeItem(String(row[idField])).catch(() => undefined);
  }
  return dropped.length;
}
